import { useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { EDITABLE_JOB_STATUSES, INTERVIEW_MODE_LABELS, INTERVIEW_STAGES, roundNameAt, type InterviewRoundInput, type InterviewStage, type JobDetail, type PlanTemplateView } from '@nest-recruit/shared';
import { Card } from '../../../../components/Card';
import { ModuleGate } from '../../../../components/ModuleGate';
import { MoreOptions } from '../../../../components/MoreOptions';
import { SkeletonLines } from '../../../../components/Skeleton';
import { errorMessage } from '../../../../lib/api-client';
import { useModule } from '../../../../lib/modules';
import { useCanEdit } from '../../../../stores/session';
import { useToasts } from '../../../../stores/toasts';
import { useApplyInterviewPlan } from '../../api/evaluation-api';
import { jobKey, useAddGuestPanelist, useInterviewOptions, useSaveJobStep } from '../../api/jobs-api';
import { useStepAutosave } from '../../hooks/use-step-autosave';
import { RoundEditor, roundErrors } from '../RoundEditor';
import { RoundSetupPlanPicker } from './RoundSetupPlanPicker';
import styles from './RoundSetup.module.css';

/**
 * C5 (ADR 0077): the Interview step's round setup, at the top of each interview stage once the job is open — how it's held, the
 * panel of up to five, and the shares. Saved as you go (the plan's own save, so the server's rules and events are step 6's). The
 * panel reaches this round's interviews still waiting for a time; ones already booked keep theirs (the planner changes a single
 * interview).
 *
 * Admin redesign batch 2 (Q2 a): the round picks an interview kit (while Interview kits is on), which reaches its interviews not
 * yet held; and "Start from a plan" (while Job templates is on) copies a saved plan's rounds into the job, with Undo — offered
 * only until a candidate reaches a round.
 */

type RoundDraft = InterviewRoundInput & { readonly id: string };

function draftsOf(job: JobDetail): readonly RoundDraft[] {
  return job.interviewRounds.map(({ stage: _stage, roundNumber: _number, isLocked: _locked, isSaved: _saved, ...round }) => round);
}

/** One line for a set-up round, so the setup can fold away and leave the list its room. `kitName` only while kits are on. */
function summaryOf(round: RoundDraft, kitName: string | null): string {
  const people = round.panel.length;
  const shares = round.panel.some((member) => member.weightPercent !== null) ? 'shares set by hand' : 'equal shares';
  return `${INTERVIEW_MODE_LABELS[round.mode]} · ${people} ${people === 1 ? 'person' : 'people'}${people > 1 ? ` · ${shares}` : ''}${kitName ? ` · ${kitName}` : ''}`;
}

export function RoundSetup({ job, stage }: { readonly job: JobDetail; readonly stage: InterviewStage }): ReactNode {
  const [rounds, setRounds] = useState<readonly RoundDraft[]>(() => draftsOf(job));
  const index = INTERVIEW_STAGES.indexOf(stage);
  const round = rounds[index];
  const view = job.interviewRounds[index];
  const options = useInterviewOptions(job.id);
  const addGuest = useAddGuestPanelist(job.id);
  const saveStep = useSaveJobStep();
  const applyPlan = useApplyInterviewPlan();
  const queryClient = useQueryClient();
  const isEditor = useCanEdit('job_config');
  const hasKits = useModule('interview_kits');
  const show = useToasts((toasts) => toasts.show);
  const dismiss = useToasts((toasts) => toasts.dismiss);
  const { state, schedule, flush } = useStepAutosave<readonly RoundDraft[]>({
    job,
    onConflict: (message) => {
      setRounds(draftsOf(job));
      show({ tone: 'danger', message: 'Someone else changed this job', detail: message });
    },
    save: (next, version) => saveStep.mutateAsync({ id: job.id, step: 'interview-plan', body: { version, rounds: [...next] } }),
  });
  if (!round || !view) return null;

  const errors = state.kind === 'error' ? roundErrors(state.fields, index) : {};
  const isReadOnly = !isEditor || !EDITABLE_JOB_STATUSES.includes(job.status);
  const isEmpty = round.panel.length === 0;
  const saveNote = state.kind === 'saving' || state.kind === 'pending' ? 'Saving…' : state.kind === 'saved' ? 'Saved' : state.kind === 'error' ? state.message : null;
  const plans = options.data?.planTemplates ?? [];
  const kitName = hasKits ? (options.data?.kits.find((kit) => kit.id === round.interviewKitId)?.name ?? null) : null;
  // The server refuses a plan once anyone has reached a round, so the button isn't offered then.
  const isPlanOffered = !isReadOnly && plans.length > 0 && !job.interviewRounds.some((candidate) => candidate.isLocked);

  function change(next: RoundDraft): void {
    const all = rounds.map((candidate, position) => (position === index ? next : candidate));
    setRounds(all);
    schedule(all);
  }

  /** The job as the cache has it now — a save just made (flushed below) moved its version on. */
  const latest = (): JobDetail => queryClient.getQueryData<JobDetail>(jobKey(job.id)) ?? job;

  async function startFrom(plan: PlanTemplateView): Promise<void> {
    try {
      // Whatever is waiting to save goes first, so the copy starts from what is on screen and Undo puts exactly that back.
      await flush();
      const before = draftsOf(latest());
      const applied = await applyPlan.mutateAsync({ jobId: job.id, body: { version: latest().version, planTemplateId: plan.id } });
      setRounds(draftsOf(applied.job));
      const leftOut = applied.leftOut.map((seat) => `${seat.fullName ?? 'Someone'} (${roundNameAt(seat.roundNumber - 1)})`);
      const toastId = show({
        tone: leftOut.length > 0 ? 'warning' : 'info',
        message: `Rounds copied from ${plan.name}`,
        ...(leftOut.length > 0 ? { detail: `Left out, as they can't interview for this job: ${leftOut.join(', ')}. Those rounds' shares are equal.` } : {}),
        action: {
          label: 'Undo',
          run: () => {
            dismiss(toastId);
            void undo(before, applied.job.version);
          },
        },
      });
    } catch (cause) {
      show({ tone: 'danger', message: `${plan.name} was not copied`, detail: errorMessage(cause) });
    }
  }

  async function undo(before: readonly RoundDraft[], version: number): Promise<void> {
    try {
      const restored = await saveStep.mutateAsync({ id: job.id, step: 'interview-plan', body: { version, rounds: [...before] } });
      setRounds(draftsOf(restored));
      show({ tone: 'info', message: 'The rounds are back as they were' });
    } catch (cause) {
      show({ tone: 'danger', message: 'The copy could not be undone', detail: errorMessage(cause) });
    }
  }

  const editor = options.isPending ? (
    <SkeletonLines count={4} />
  ) : (
    <RoundEditor
      round={round}
      index={index}
      options={options.data}
      isReadOnly={isReadOnly}
      isLocked={view.isLocked}
      errors={errors}
      isListItem={false}
      hasKitPicker
      onChange={(next) => change({ ...next, id: round.id })}
      onAddGuest={(input) => addGuest.mutateAsync(input)}
    />
  );

  const action = (
    <span className={styles.actions}>
      {saveNote && (
        <span className={state.kind === 'error' ? styles.saveError : styles.saveNote} role="status">
          {saveNote}
        </span>
      )}
      {isPlanOffered && (
        <ModuleGate module="templates">
          <RoundSetupPlanPicker plans={plans} rounds={rounds} isBusy={applyPlan.isPending || saveStep.isPending} onApply={(plan) => void startFrom(plan)} />
        </ModuleGate>
      )}
    </span>
  );

  return (
    <Card title={`${roundNameAt(index)} setup`} meta={isEmpty ? 'Add the panel. Each person gets their own link to give a mark from 1 to 5' : summaryOf(round, kitName)} action={action}>
      {isEmpty ? editor : <MoreOptions label={hasKits ? "Change how it's held, the panel, the shares or the kit" : "Change how it's held, the panel or the shares"}>{editor}</MoreOptions>}
    </Card>
  );
}
